import { Injectable } from '@angular/core';
import { ChartCardConfig, ChartType } from '../models/chart-config.model';
import { chartHasTimeDimension } from '../utils/chart-card-date-range.util';
import {
	ValidationWarning,
	WarningSeverity,
	ConfigChange,
	ChartTypeRequirements,
	CHART_TYPE_REQUIREMENTS,
	chartTypeLabel,
} from './chart-config-change.types';

/**
 * Validates chart configurations against chart-type requirements.
 * Part of the unified pipeline - all validation logic lives here.
 */
@Injectable({
	providedIn: 'root',
})
export class ChartConfigValidator {
	/**
	 * Validates a chart configuration and returns warnings.
	 * Does not throw - returns warnings for caller to handle.
	 *
	 * @param config - Current chart configuration
	 * @returns Array of validation warnings (may be empty)
	 */
	validate(config: ChartCardConfig): ValidationWarning[] {
		const warnings: ValidationWarning[] = [];

		if (!config.chartType) {
			warnings.push({
				code: 'MISSING_CHART_TYPE',
				message: 'Chart type is required',
				severity: WarningSeverity.Blocking,
			});
			return warnings;
		}

		const requirements = CHART_TYPE_REQUIREMENTS[config.chartType];
		if (!requirements) {
			warnings.push({
				code: 'UNKNOWN_CHART_TYPE',
				message: `Unknown chart type: ${config.chartType}`,
				severity: WarningSeverity.Blocking,
			});
			return warnings;
		}

		// Validate data configuration
		warnings.push(...this.validateData(config, requirements));

		// Validate visualization coherence
		warnings.push(...this.validateVisualization(config));

		return warnings;
	}

	/**
	 * Validates data configuration against chart type requirements.
	 */
	private validateData(
		config: ChartCardConfig,
		requirements: ChartTypeRequirements
	): ValidationWarning[] {
		const warnings: ValidationWarning[] = [];
		const data = config.data;
		const metrics = data?.metrics ?? [];
		const dimensions = data?.dimensions ?? [];
		const hasMetric = metrics.some(
			m => typeof m?.name === 'string' && m.name.trim().length > 0
		);
		const hasDimension = dimensions.some(
			d => typeof d?.name === 'string' && d.name.trim().length > 0
		);

		if (!hasMetric && !hasDimension) {
			warnings.push({
				code: 'CHART_NO_DIMENSION_OR_METRIC',
				message:
					'Add at least one dimension or metric in the Data tab.',
				severity: WarningSeverity.Blocking,
				field: 'data.dimensions',
			});
			return warnings;
		}

		if (!data) {
			return warnings;
		}

		// Validate metrics count
		const metricsCount = data.metrics?.length ?? 0;
		const label = chartTypeLabel(config.chartType);

		if (metricsCount < requirements.minMetrics) {
			warnings.push({
				code: 'INSUFFICIENT_METRICS',
				message: `${label} needs at least ${requirements.minMetrics} metric${requirements.minMetrics === 1 ? '' : 's'} (${metricsCount} selected).`,
				severity: WarningSeverity.Warning,
				field: 'data.metrics',
				suggestedAction: 'Add metrics in the Data tab',
			});
		}

		if (
			requirements.maxMetrics !== undefined &&
			metricsCount > requirements.maxMetrics
		) {
			warnings.push({
				code: 'EXCESS_METRICS',
				message: `${label} supports up to ${requirements.maxMetrics} metric${requirements.maxMetrics === 1 ? '' : 's'} (${metricsCount} selected). Extra metrics may not display.`,
				severity: WarningSeverity.Warning,
				field: 'data.metrics',
				suggestedAction: 'Remove extra metrics in the Data tab',
			});
		}

		// Validate time dimension for time-series charts (singular or timeDimensions[0])
		if (requirements.timeSeries && !chartHasTimeDimension(config)) {
			warnings.push({
				code: 'MISSING_TIME_DIMENSION',
				message: `${label} needs a date axis. Configure it in the Data tab.`,
				severity: WarningSeverity.Warning,
				field: 'data.timeDimension',
				suggestedAction: 'Open the Data tab and set the date range',
			});
		}

		// Validate category dimension for category-based charts
		if (
			requirements.usesCategory &&
			(!data.dimensions || data.dimensions.length === 0)
		) {
			warnings.push({
				code: 'MISSING_DIMENSION',
				message: `${label} works best with a category dimension.`,
				severity: WarningSeverity.Info,
				field: 'data.dimensions',
				suggestedAction: 'Add a dimension in the Data tab',
			});
		}

		return warnings;
	}

	/**
	 * Validates visualization configuration coherence.
	 */
	private validateVisualization(
		config: ChartCardConfig
	): ValidationWarning[] {
		const warnings: ValidationWarning[] = [];
		const viz = config.visualization;

		if (!viz) {
			return warnings;
		}

		// Check for chart.type mismatch
		const chartObj = viz?.chart as Record<string, unknown> | undefined;
		const vizChartType = chartObj?.type as string | undefined;
		if (vizChartType && vizChartType !== config.chartType) {
			warnings.push({
				code: 'VISUALIZATION_CHART_TYPE_MISMATCH',
				message: `Visualization has chart.type="${vizChartType}" but config has chartType="${config.chartType}"`,
				severity: WarningSeverity.Warning,
				field: 'visualization.chart.type',
				suggestedAction:
					'The visualization chart type will be normalized to match the config',
			});
		}

		return warnings;
	}

	/**
	 * Sanitizes visualization config to ensure it's a valid object (not a string).
	 * Does NOT modify chart.type — that's managed by the settings component when
	 * there are actual visualization overrides.
	 *
	 * @param visualization - Current visualization config (may be object or AWSJSON string)
	 * @returns Sanitized visualization config
	 */
	sanitizeVisualization(
		visualization: ChartCardConfig['visualization']
	): ChartCardConfig['visualization'] {
		if (!visualization) {
			return visualization;
		}

		// Normalize stringified visualization (e.g., from AppSync AWSJSON)
		// Type assertion needed because runtime can receive AWSJSON strings
		// even though the type annotation is Highcharts.Options
		const vizValue = visualization as unknown;

		if (typeof vizValue === 'string') {
			const trimmed = (vizValue as string).trim();
			if (trimmed === '') {
				return {};
			}
			try {
				return JSON.parse(trimmed) as ChartCardConfig['visualization'];
			} catch {
				// If string is not valid JSON, treat it as an empty visualization
				return {};
			}
		} else if (
			typeof vizValue === 'object' &&
			vizValue !== null &&
			!Array.isArray(vizValue)
		) {
			return {
				...(vizValue as Record<string, unknown>),
			} as ChartCardConfig['visualization'];
		}

		// Invalid visualization type, return empty object
		return {};
	}

	/**
	 * Checks if a change introduces any breaking issues.
	 * Used before applying changes to surface blocking problems early.
	 *
	 * @param current - Current config
	 * @param change - Proposed change
	 * @returns Warnings including any blocking issues
	 */
	validateChange(
		current: ChartCardConfig,
		change: ConfigChange
	): ValidationWarning[] {
		// Merge to get prospective config
		const prospective: ChartCardConfig = {
			...current,
			...change.patch,
			data: change.patch.data
				? { ...(current.data ?? {}), ...change.patch.data }
				: current.data,
			visualization: change.patch.visualization
				? {
						...(current.visualization ?? {}),
						...change.patch.visualization,
					}
				: current.visualization,
		};

		return this.validate(prospective);
	}

	/**
	 * Checks if a chart type switch requires data migration.
	 *
	 * @param from - Source chart type
	 * @param to - Target chart type
	 * @returns Whether migration is recommended
	 */
	requiresMigration(from: string, to: string): boolean {
		if (from === to) {
			return false;
		}

		const fromReqs = CHART_TYPE_REQUIREMENTS[from];
		const toReqs = CHART_TYPE_REQUIREMENTS[to];

		if (!fromReqs || !toReqs) {
			return true; // Unknown type, safer to migrate
		}

		// Migration needed if time series requirement changes
		if (fromReqs.timeSeries !== toReqs.timeSeries) {
			return true;
		}

		// Migration needed if dimension usage changes
		if (fromReqs.usesCategory !== toReqs.usesCategory) {
			return true;
		}

		// Migration needed if metric count constraints differ significantly
		if (fromReqs.maxMetrics !== toReqs.maxMetrics) {
			return true;
		}

		return false;
	}
}
